import { Injectable, NgZone } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '@environment';
import { map, finalize } from 'rxjs/operators';
import { ErrorEnum } from '../enums/error.enum';
import { LogOffOptions } from '@sb-tools/typescript/authentication/log-off-options.model';
import { Observable } from 'rxjs/internal/Observable';
import { of } from 'rxjs/internal/observable/of';
import { OutlookUserService } from '@form-field-module/common/services/outlook-user.service';
import { LocalStorageService } from '@sb-tools/typescript/services/local-storage.service';

// todo: use auth service from sb-tools

@Injectable()
export class AuthenticationService {
    isLoggingOut: boolean;
    needToRefreshToken: boolean;
    refreshPromise;
    token: string;

    private accessTokenKey: string;
    private accessTokensKey: string;
    private expiresKey: string;
    private loginStatus: string;
    private refreshTokenKey: string;
    private timeDifference = 0;
    private timeDifferenceKey: string;
    private tokenExpires: Date;

    constructor(
        private httpClient: HttpClient,
        private localStorageService: LocalStorageService,
        private outlookUserService: OutlookUserService,
        private zone: NgZone,
    ) {
        this.refreshTokenKey = environment.environment + '_refresh_token_outloook';
        this.accessTokenKey = environment.environment + '_access_token_outloook';
        this.accessTokensKey = environment.environment + '_access_tokens_outloook';
        this.expiresKey = environment.environment + '_access_token_expires_outloook';
        this.loginStatus = environment.environment + '_is_logged_in_outloook';

        this.token = this.getAccessToken();
        this.tokenExpires = new Date(this.localStorageService.getItem(this.expiresKey));

        this.timeDifferenceKey = environment.environment + '_timeDifference_outloook';
        this.timeDifference = +this.localStorageService.getItem(this.timeDifferenceKey);
        if (isNaN(this.timeDifference)) {
            this.timeDifference = 0;
        }
    }

    getAuthorizationHeader(isStayOnCurrentUrlAfterLogOut = false): Promise<any> {
        // if localhost on IE
        if (environment.isLocalhost && this.isIe()) {
            // tslint:disable-next-line:max-line-length

            // for demo (user pogorelovat.i@gmail.com):
            // eyJhbGciOiJIUzUxMiIsInR5cCI6IkpXVCJ9.eyJodHRwOi8vc2NoZW1hcy54bWxzb2FwLm9yZy93cy8yMDA1LzA1L2lkZW50aXR5L2NsYWltcy9uYW1laWRlbnRpZmllciI6ImYwOWVhMjBjLTZhMDAtNGYzOC1iOWNjLTc4ZTgwMDQ0MjQwNyIsImh0dHA6Ly9zY2hlbWFzLnhtbHNvYXAub3JnL3dzLzIwMDUvMDUvaWRlbnRpdHkvY2xhaW1zL25hbWUiOiJwb2dvcmVsb3ZhdC5pQGdtYWlsLmNvbSIsImh0dHA6Ly9zY2hlbWFzLm1pY3Jvc29mdC5jb20vYWNjZXNzY29udHJvbHNlcnZpY2UvMjAxMC8wNy9jbGFpbXMvaWRlbnRpdHlwcm92aWRlciI6IkFTUC5ORVQgSWRlbnRpdHkiLCJBc3BOZXQuSWRlbnRpdHkuU2VjdXJpdHlTdGFtcCI6IjcyNTQwNTZlLWNhMGYtNGE2MC1hYmI0LTJmOGIyM2Q0MGE1NiIsInR5cGUiOiJVc2VyIiwic2lkIjoiNzBiMzMyNTFkMzA3NGU5NTgwZDllMmM0MGQ4NDQxYzIiLCJodHRwOi8vc2NoZW1hcy5taWNyb3NvZnQuY29tL3dzLzIwMDgvMDYvaWRlbnRpdHkvY2xhaW1zL3JvbGUiOiJBZG1pbiIsImNvbW11bml0eSI6ImRlbW8iLCJzY29wZSI6WyJJbmJveCIsIkZBUSIsIkdEUFJQb3J0YWwiXSwibmJmIjoxNTY0MTQ0NDU2LCJleHAiOjE4MjMzNDQ0NTYsImlzcyI6InNvY2lhbGJvYXJkcy5jb20iLCJhdWQiOiI0MTRlMTkyN2EzODg0ZjY4YWJjNzlmNzI4MzgzN2ZkMSJ9.Pjmf334zKSLrSBOWrzIjfZ9WExQUkK87uKh_Db2f4vxZ1YQmWzv4UOsjOT4qlBrWMBSOvVpOuLFVscTmwFLO6g
            //
            // for seleniumtests:
            // eyJhbGciOiJIUzUxMiIsInR5cCI6IkpXVCJ9.eyJodHRwOi8vc2NoZW1hcy54bWxzb2FwLm9yZy93cy8yMDA1LzA1L2lkZW50aXR5L2NsYWltcy9uYW1laWRlbnRpZmllciI6ImYwOWVhMjBjLTZhMDAtNGYzOC1iOWNjLTc4ZTgwMDQ0MjQwNyIsImh0dHA6Ly9zY2hlbWFzLnhtbHNvYXAub3JnL3dzLzIwMDUvMDUvaWRlbnRpdHkvY2xhaW1zL25hbWUiOiJwb2dvcmVsb3ZhdC5pQGdtYWlsLmNvbSIsImh0dHA6Ly9zY2hlbWFzLm1pY3Jvc29mdC5jb20vYWNjZXNzY29udHJvbHNlcnZpY2UvMjAxMC8wNy9jbGFpbXMvaWRlbnRpdHlwcm92aWRlciI6IkFTUC5ORVQgSWRlbnRpdHkiLCJBc3BOZXQuSWRlbnRpdHkuU2VjdXJpdHlTdGFtcCI6IjcyNTQwNTZlLWNhMGYtNGE2MC1hYmI0LTJmOGIyM2Q0MGE1NiIsInR5cGUiOiJVc2VyIiwic2lkIjoiNzBiMzMyNTFkMzA3NGU5NTgwZDllMmM0MGQ4NDQxYzIiLCJodHRwOi8vc2NoZW1hcy5taWNyb3NvZnQuY29tL3dzLzIwMDgvMDYvaWRlbnRpdHkvY2xhaW1zL3JvbGUiOiJBZG1pbiIsImNvbW11bml0eSI6InNlbGVuaXVtdGVzdHMiLCJzY29wZSI6IkluYm94IiwibmJmIjoxNTY0MTQ0NTQ3LCJleH
            const header =
                'Bearer eyJhbGciOiJIUzUxMiIsInR5cCI6IkpXVCJ9.eyJodHRwOi8vc2NoZW1hcy54bWxzb2FwLm9yZy93cy8yMDA1LzA1L2lkZW50aXR5L2NsYWltcy9uYW1laWRlbnRpZmllciI6IjE5NTdmODY0LTM3NDQtNGEyZC1iMDg0LTI3MjFiMDQxZTRlMSIsImh0dHA6Ly9zY2hlbWFzLnhtbHNvYXAub3JnL3dzLzIwMDUvMDUvaWRlbnRpdHkvY2xhaW1zL25hbWUiOiJtYXJpYUBzb2NpYWxib2FyZHMuY29tIiwiaHR0cDovL3NjaGVtYXMubWljcm9zb2Z0LmNvbS9hY2Nlc3Njb250cm9sc2VydmljZS8yMDEwLzA3L2NsYWltcy9pZGVudGl0eXByb3ZpZGVyIjoiQVNQLk5FVCBJZGVudGl0eSIsIkFzcE5ldC5JZGVudGl0eS5TZWN1cml0eVN0YW1wIjoiM2QxZWU2OWEtZjFkZS00MzcwLThjNTItMTRkZDYxMDFjYWRkIiwidHlwZSI6IlVzZXIiLCJzaWQiOiJiODM3ZDkzNGQ5MWQ0ZGE4YmFkMmU2YjU1YzMxNDlkYiIsImh0dHA6Ly9zY2hlbWFzLm1pY3Jvc29mdC5jb20vd3MvMjAwOC8wNi9pZGVudGl0eS9jbGFpbXMvcm9sZSI6IkFkbWluIiwiY29tbXVuaXR5IjoiZGVtbyIsInNjb3BlIjpbIkluYm94IiwiRkFRIiwiR0RQUlBvcnRhbCJdLCJuYmYiOjE1NjQxNDQ4MDYsImV4cCI6MTgyMzM0NDgwNiwiaXNzIjoic29jaWFsYm9hcmRzLmNvbSIsImF1ZCI6IjQxNGUxOTI3YTM4ODRmNjhhYmM3OWY3MjgzODM3ZmQxIn0.yNMYR4llZxj_n7ke-AC0YjRPFF2CGPx-6ieh4iaS1Kc8tfFm5-FEZTXW1bz0Vw-dio3VzLdOEGqxprDKmqoHAQ';
            return of(header).toPromise();
        }

        if (!this.getRefreshToken()) {
            this.logOff(); // ?
            return of('').toPromise();
        }

        if (!this.getAccessToken() || this.isTokenExpired() || this.needToRefreshToken) {
            if (!this.refreshPromise) {
                this.refreshPromise = this.refreshToken().toPromise();
            }

            return this.refreshPromise.then(
                (data) => {
                    return of(data).toPromise();
                },
                (response) => {
                    if (response.error?.errorId === ErrorEnum.authorizationInfoIsBroken) {
                        const rereshTokenFromCookies = this.getRefreshToken();
                        const refreshTokenFromRequest = response.error.debugInfo.token;

                        if (
                            rereshTokenFromCookies &&
                            refreshTokenFromRequest &&
                            refreshTokenFromRequest !== rereshTokenFromCookies &&
                            this.getAccessToken()
                        ) {
                            const header = 'Bearer ' + this.getAccessToken();

                            return of(header).toPromise();
                        } else {
                            this.logOff({ isStayOnCurrentUrl: isStayOnCurrentUrlAfterLogOut });
                        }
                    } else {
                        this.logOff({ isStayOnCurrentUrl: isStayOnCurrentUrlAfterLogOut });
                    }
                },
            );
        } else {
            const header = 'Bearer ' + this.getAccessToken();

            return of(header).toPromise();
        }
    }

    getToken(type: 'token' | 'code', data): Promise<object> {
        const body = new URLSearchParams();

        if (type === 'token') {
            body.set('grant_type', 'aad_token');
            body.set('bootstrap_token', data);
        } else {
            body.set('grant_type', 'authorization_code');
            body.set('code', data);
        }

        body.set('client_id', 'outlookAddin');

        const options = {
            headers: new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded').delete('DNT'),
        };

        return this.httpClient
            .post<object>(environment.apiAuthUrl + 'oauth/token', body.toString(), options)
            .toPromise()
            .then((authData) => {
                this.updateAuthData(authData);

                return of(authData).toPromise();
            });
    }

    isLoggedIn(): boolean {
        return Boolean(this.getAccessToken()) && this.localStorageService.getItem(this.loginStatus) === 'true';
    }

    logOff(options: LogOffOptions = {}) {
        console.log('logOff');
        setTimeout(() => {
            this.isLoggingOut = true;
            if (this.getRefreshToken()) {
                const body = new URLSearchParams();
                body.set('token', this.getRefreshToken());

                const requestOptions = {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/x-www-form-urlencoded',
                    }),
                };

                this.httpClient
                    .post<object>(environment.apiAuthUrl + 'oauth/revoke/', body.toString(), requestOptions)
                    .pipe(
                        finalize(() => {
                            this.logOffCallback(options);
                        }),
                    )
                    .subscribe();
            } else {
                this.logOffCallback(options);
            }
        }, 200);
    }

    // return difference in milliseconds between client and server time
    setTimeDifference(dateString): void {
        if (dateString) {
            this.timeDifference = new Date().getTime() - new Date(dateString).getTime();
            this.localStorageService.setItem(this.timeDifferenceKey, this.timeDifference.toString());
        }
    }

    redirectToCommunities() {
        window.location.href = environment.loginAppUrl + '/communities';
    }

    refreshToken(): Observable<string> {
        const refreshToken = this.getRefreshToken();

        const body = new URLSearchParams();
        body.set('refresh_token', refreshToken);
        body.set('grant_type', 'refresh_token');
        body.set('client_id', 'outlookAddin');

        const options = {
            headers: new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded').delete('DNT'),
        };

        return this.httpClient.post<object>(environment.apiAuthUrl + 'oauth/token', body.toString(), options).pipe(
            map((data) => {
                this.updateAuthData(data);

                this.refreshPromise = null;

                this.needToRefreshToken = false;

                return 'Bearer ' + data['access_token'];
            }),
        );
    }

    updateAuthData(data): void {
        this.localStorageService.setItem(this.refreshTokenKey, data['refresh_token']);
        this.localStorageService.setItem(this.accessTokenKey, data['access_token']);
        this.localStorageService.setItem(this.expiresKey, data['.expires']);
        this.localStorageService.setItem(this.loginStatus, 'true');

        this.token = data['access_token'];
        this.tokenExpires = new Date(this.localStorageService.getItem(this.expiresKey));
    }

    private getRefreshToken(): string {
        return this.localStorageService.getItem(this.refreshTokenKey);
    }

    private getTokenExpiresDate(): Date {
        return this.tokenExpires;
    }

    public getAccessToken(): string {
        return this.localStorageService.getItem(this.accessTokenKey) || this.token;
    }

    private isIe() {
        const sAgent = window.navigator.userAgent;
        const Idx = sAgent.indexOf('MSIE');

        // If IE, return version number.
        if (Idx > 0) {
            return true; // parseInt(sAgent.substring(Idx + 5, sAgent.indexOf(".", Idx)));

            // If IE 11 then look for Updated user agent string.
        } else if (navigator.userAgent.match(/Trident\/7\./)) {
            return true; // 11;
        }

        return false; // It is not IE
    }

    private logOffCallback(options: LogOffOptions = {}) {
        this.localStorageService.removeItem(this.accessTokenKey);
        this.localStorageService.removeItem(this.accessTokensKey);
        this.localStorageService.removeItem(this.expiresKey);
        this.localStorageService.removeItem(this.refreshTokenKey);
        this.localStorageService.removeItem(this.timeDifferenceKey);
        this.localStorageService.removeItem(this.refreshTokenKey);

        this.localStorageService.setItem(this.loginStatus, 'false');

        // zone.run is needed here because code is run out of angular zone (in office js)
        setTimeout(() => {
            this.zone.run(() => {
                this.outlookUserService.isLoggedIn = false;
            });

            if (options.callback) {
                options.callback();
            }

            this.refreshPromise = null;
        }, 50);
    }

    private isTokenExpired(): boolean {
        return this.getTokenExpiresDate().getTime() - new Date().getTime() + this.timeDifference < 60000;
    }
}
